import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import TypeSearch from '../../components/common/TypeSearch';

export default function SupplierDetail() {
  const { id } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [account, setAccount] = useState(null);
  const [parent, setParent] = useState(null);

  // Sub-suppliers this supplier actually settles on behalf of — e.g. IATA/Voyzant
  // consolidate payment for many individual airlines, RAJABDEEN HAJI for several land
  // operators like ARABIAN LUX. These are ordinary existing suppliers, just re-parented:
  // ticket/cost entry can then pick this supplier as who actually gets paid, and the
  // sub-supplier separately for the record — the AP stays tagged to this supplier only.
  const [subSuppliers, setSubSuppliers] = useState([]);
  const [pickLabel, setPickLabel] = useState('');
  const [linking, setLinking] = useState(false);
  const [showNewSub, setShowNewSub] = useState(false);
  const [newSubName, setNewSubName] = useState('');

  const loadSubSuppliers = useCallback(async () => {
    const { data } = await supabase.from('suppliers').select('id, name, supplier_code').eq('parent_supplier_id', id).eq('is_active', true).order('name');
    setSubSuppliers(data || []);
  }, [id]);

  useEffect(() => {
    supabase.from('suppliers').select('*').eq('id', id).single().then(({ data }) => {
      setSupplier(data);
      if (data?.account_id) {
        supabase.from('chart_of_accounts').select('account_code, account_name').eq('id', data.account_id).single().then(({ data: a }) => setAccount(a));
      }
      if (data?.parent_supplier_id) {
        supabase.from('suppliers').select('id, name').eq('id', data.parent_supplier_id).single().then(({ data: p }) => setParent(p));
      } else {
        setParent(null);
      }
    });
    loadSubSuppliers();
  }, [id, loadSubSuppliers]);

  const linkSubSupplier = async (supplierId) => {
    setLinking(true);
    await supabase.from('suppliers').update({ parent_supplier_id: id }).eq('id', supplierId);
    setPickLabel('');
    await loadSubSuppliers();
    setLinking(false);
  };

  const unlinkSubSupplier = async (supplierId) => {
    await supabase.from('suppliers').update({ parent_supplier_id: null }).eq('id', supplierId);
    await loadSubSuppliers();
  };

  const createAndLinkSubSupplier = async () => {
    if (!newSubName.trim()) return;
    setLinking(true);
    const code = 'SUP' + Date.now().toString().slice(-5);
    const { data: s, error } = await supabase.from('suppliers')
      .insert({ name: newSubName.trim(), supplier_code: code, supplier_type: supplier.supplier_type, parent_supplier_id: id, is_active: true })
      .select('id').single();
    if (!error && s?.id) await loadSubSuppliers();
    setNewSubName(''); setShowNewSub(false);
    setLinking(false);
  };

  if (!supplier) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{supplier.name}</div>
          <div className="page-subtitle">
            {supplier.supplier_code}  {supplier.supplier_type}
            {parent && <> — sub-supplier of <Link to={`/suppliers/${parent.id}`}>{parent.name}</Link></>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link to={`/suppliers/${id}/edit`} className="btn btn-secondary">Edit</Link>
          <Link to={`/ledger/suppliers`} state={{ supplierId: id }} className="btn btn-secondary">📒 Ledger</Link>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>Supplier Details</div>
          {[
            ['Type', supplier.supplier_type],
            ['Contact', supplier.contact_person],
            ['Email', supplier.email],
            ['Phone', supplier.phone],
            ['Country', supplier.country],
            ['Payment Terms', supplier.payment_terms],
            ['COA Account', account ? `${account.account_code}  ${account.account_name}` : null],
            ['Status', supplier.is_active ? 'Active' : 'Inactive'],
          ].map(([l, v]) => v ? (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--text-muted)' }}>{l}</span>
              <span style={{ fontWeight: 500 }}>{v}</span>
            </div>
          ) : null)}
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom: 16 }}>Notes</div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{supplier.notes || 'No notes.'}</p>

          {(supplier.created_by || supplier.updated_by) && (
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div className="card-title" style={{ marginBottom: 8 }}>Audit</div>
              {supplier.created_by && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Created by <strong>{supplier.created_by}</strong></div>}
              {supplier.updated_by && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Last edited by <strong>{supplier.updated_by}</strong> on {supplier.updated_at?.slice(0, 10)}</div>}
            </div>
          )}
        </div>
      </div>

      {/* Sub-suppliers this supplier actually pays on behalf of — e.g. IATA/Voyzant for
          individual airlines, RAJABDEEN HAJI for land operators like ARABIAN LUX. Ticket/cost
          entry screens use this list: once this supplier is picked as "who gets paid", staff
          sub-select the real sub-supplier separately, purely for record-keeping — the AP
          stays tagged to this supplier, never the sub-supplier. */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-title" style={{ marginBottom: 4 }}>Sub-Suppliers Settled Through {supplier.name}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
          Sub-suppliers are a grouping label only — we owe {supplier.name}, never the sub-supplier. On cost entry, picking {supplier.name} shows a sub-supplier dropdown; the cost and payable always post to {supplier.name}, with the sub-supplier's name kept on the line for reference. Sub-suppliers don't appear in the main supplier list or in payment vouchers.
        </div>

        {subSuppliers.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {subSuppliers.map(s => (
              <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 20, background: 'var(--bg)', border: '1px solid var(--border)', fontSize: 13 }}>
                <Link to={`/suppliers/${s.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>{s.name}</Link>
                <button onClick={() => unlinkSubSupplier(s.id)} title="Remove"
                  style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
              </span>
            ))}
          </div>
        )}
        {subSuppliers.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>No sub-suppliers linked yet — this supplier will act as a normal, standalone supplier on ticket/cost entry.</div>
        )}

        {!showNewSub ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, maxWidth: 320 }}>
              <TypeSearch
                fetchFn={async (q) => {
                  const { data } = await supabase.from('suppliers').select('id, name, supplier_code').eq('is_active', true).neq('id', id).ilike('name', `%${q}%`).order('name').limit(20);
                  return (data || []).map(s => ({ ...s, label: `${s.name} (${s.supplier_code})` }));
                }}
                value={pickLabel}
                displayKey="label"
                placeholder="Search an existing supplier…"
                onSelect={s => { setPickLabel(s.label); linkSubSupplier(s.id); }}
                onClear={() => setPickLabel('')}
              />
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowNewSub(true)} disabled={linking}>+ New Supplier</button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Supplier Name</label>
              <input className="form-input" value={newSubName} onChange={e => setNewSubName(e.target.value)} placeholder="e.g. Arabian Lux" />
            </div>
            <button className="btn btn-primary btn-sm" onClick={createAndLinkSubSupplier} disabled={linking || !newSubName.trim()}>{linking ? 'Adding…' : 'Add'}</button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowNewSub(false); setNewSubName(''); }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
